import { z } from 'zod';
import { AuditEntrySchema, ChatMessageSchema } from './actors-chat-audit.js';
import { enforceActiveStrictness, RequestObjectBase } from './_base.js';
import { isDraftLikeRequestStatus, RequestStatus } from './status.js';

export const RequestSchema = RequestObjectBase.superRefine(enforceActiveStrictness);
export type Request = z.infer<typeof RequestSchema>;

// WHY (#1662, staged rollout): the `GET /requests` LIST wire may OMIT
//   `chat`/`audit` per row — the web never reads them from list rows and
//   shipping every row's full history was the PERF-1 cost. This row schema
//   relaxes ONLY those two keys to optional so a list response parses with
//   or without them; the DETAIL `RequestSchema` above keeps REQUIRING them.
//   Stale browser bundles compiled before this expand parse list rows that
//   still carry chat/audit (Stage 1 backend default emits them); Stage 2
//   flips the backend default to omit. Both keys optional = both bundle
//   generations parse both response shapes.
export const RequestListRowSchema = RequestObjectBase.extend({
  chat: ChatMessageSchema.array().optional(),
  audit: AuditEntrySchema.array().optional(),
}).superRefine(enforceActiveStrictness);
export type RequestListRow = z.infer<typeof RequestListRowSchema>;

// WHY: status-aware sibling parsers exposed for callers that want a hard
// guarantee about which side of the discriminator they are looking at.
// `DraftRequestSchema` accepts only `status: DRAFT` and skips the
// active-strictness refinement; `ActiveRequestSchema` accepts only
// non-draft-like statuses and re-asserts the active strictness. The shared
// `RequestSchema` accepts either side and delegates strictness to
// `enforceActiveStrictness` based on the runtime `status`. `RETURN` is a
// draft-like status (see `DRAFT_LIKE_REQUEST_STATUSES`), so it round-trips
// through `RequestSchema` with relaxed strictness even though
// `DraftRequestSchema` keeps its narrow literal `DRAFT` discriminator.
export const DraftRequestSchema = RequestObjectBase.extend({
  status: z.literal(RequestStatus.DRAFT),
});
export type DraftRequest = z.infer<typeof DraftRequestSchema>;

const ActiveRequestStatusSchema = z
  .nativeEnum(RequestStatus)
  .refine((s) => !isDraftLikeRequestStatus(s), { message: 'expected non-draft-like status' });

export const ActiveRequestSchema = RequestObjectBase.extend({
  status: ActiveRequestStatusSchema,
}).superRefine(enforceActiveStrictness);
export type ActiveRequest = z.infer<typeof ActiveRequestSchema>;
